import React from "react";

import { OUTCOME_JSON, OUTCOME_NONE, resolveSlotOutcome } from "../../utils/adConfig";
import { useAuctionOutcome } from "../../utils/adAuction";
import { CodeAdapter } from "./CodeAdapter";
import { SspAdapter } from "./SspAdapter";
import { YandexAdapter } from "./YandexAdapter";
import SmiWidget from "../smiWidgets/SmiWidget";
import MoeVideo from "../smiWidgets/MoeVideo";

const FLOATING_SLOT = "floorad";

/**
 * Единая точка входа рендера рекламного слота.
 *
 * Вызов знает только имя слота: провайдер, вариант и участие в ротации уже
 * решены — провайдер полем в БД, вариант резолвером, плечо ротации сервером.
 *
 * `render` нужен слотам, у которых компонент шаблона не сводится к адаптеру
 * провайдера: MoeVideo в разных шаблонах разный и принимает свои пропы. Запись
 * берётся из БД, а рисует её шаблон сам.
 *
 * Нет конфига или слота в нём — ничего не рисуется. json-место рисует проп `json`.
 *
 * Запись с `auction` — участник аукциона RTB против соперника: она ждёт исхода и
 * рендерится, только если выиграла её роль. Шаблоны про аукцион не знают.
 */
export function AdSlot({ slot, isFull, isDesktop, json, render = null, wrap = null }) {
    const outcome = resolveSlotOutcome(slot, { isFull, isDesktop });

    // json-место рисует тизерный компонент шаблона; без пропа — ничего.
    if (outcome.kind === OUTCOME_JSON) {
        return json === undefined ? null : json;
    }

    // Слот не заведён или плечо ротации проиграло.
    if (outcome.kind === OUTCOME_NONE) {
        return null;
    }

    const { entries } = outcome;

    const content = (
        <React.Fragment>
            {entries.map((entry, index) => {
                // Код соперника рисует адаптер: render шаблона знает про блоки провайдеров, а не про код.
                const entryContent =
                    render && entry.provider !== "code" ? (
                        render(entry)
                    ) : (
                        <AdEntry
                            entry={entry}
                            isFull={isFull}
                            isDesktop={isDesktop}
                            floating={slot === FLOATING_SLOT}
                            closable={slot === FLOATING_SLOT}
                        />
                    );

                return entry.auction ? (
                    <AuctionGate key={`${slot}-${index}`} entry={entry}>
                        {entryContent}
                    </AuctionGate>
                ) : (
                    <React.Fragment key={`${slot}-${index}`}>{entryContent}</React.Fragment>
                );
            })}
        </React.Fragment>
    );

    return wrap ? wrap(content) : content;
}

// До исхода и при проигрыше — ничего, в том числе без SDK провайдера:
// проигравший участник — это то же, что проигравшее плечо ротации.
function AuctionGate({ entry, children }) {
    const outcome = useAuctionOutcome(entry.auction);

    if (!outcome || outcome.winner !== entry.auctionRole) {
        return null;
    }

    return children;
}

function AdEntry({ entry, isFull, isDesktop, floating, closable }) {
    if (entry.provider === "code") {
        return <CodeAdapter entry={entry} floating={floating} />;
    }

    if (entry.provider === "yandex") {
        return <YandexAdapter entry={entry} />;
    }

    // smi-блок в аукционе рисуется не как обычный информер, а кодом выигравшей ставки RTB.
    if (entry.auctionRole === "ssp") {
        return <SspAdapter entry={entry} floating={floating} closable={closable} />;
    }

    if (entry.provider === "moevideo") {
        return <MoeVideo placements={entry.blockId} isFull={isFull} isDesktop={isDesktop} />;
    }

    return <SmiWidget blockIds={entry.blockId} isFull={isFull} isDesktop={isDesktop} />;
}

export default AdSlot;
